import type { FieldKind } from './fields.js';
import { isIsoDate, isoOfDate } from './isoDate.js';

/**
 * A value as it reads in a diff or a record view. Total: any input gives a string, and nothing is
 * ever shown as JSON.
 */
export const NOT_SET = 'Not set';
export const NONE = 'None';
/**
 * An empty string, which is a value, not the absence of one: a diff between `''` and `null` is a
 * real difference (it hashes differently and a push writes it), so the two must not both read
 * "Not set" — the row would claim a change between two identical-looking sides.
 */
export const EMPTY = 'Empty';

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

const dateText = (date: Date): string => (Number.isNaN(date.getTime()) ? NOT_SET : isoOfDate(date));

const stringText = (text: string, kind?: FieldKind): string => {
    if (text === '') return EMPTY;
    if (ISO_DATE_TIME.test(text)) return text.slice(0, 10);
    if (kind === 'date' && !isIsoDate(text)) {
        const parsed = new Date(text);
        return Number.isNaN(parsed.getTime()) ? text : dateText(parsed);
    }
    return text;
};

export const valueText = (v: unknown, kind?: FieldKind): string => {
    if (v === undefined || v === null) return NOT_SET;
    if (typeof v === 'string') return stringText(v, kind);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return String(v);
    if (v instanceof Date) return dateText(v);
    if (Array.isArray(v)) return v.length ? v.map((item) => valueText(item)).join(', ') : NONE;
    if (typeof v === 'object') {
        const entries = Object.entries(v as Record<string, unknown>);
        return entries.length ? entries.map(([key, value]) => `${key}: ${valueText(value)}`).join(', ') : NONE;
    }
    try {
        return String(v);
    } catch {
        return NOT_SET;
    }
};
